import * as React from 'react';
import * as _ from 'underscore';

import { isNullOrUndefined } from 'util';
import { Messages } from '../../../codegen/Localize';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Filter } from '../../../ts/helpers/Filter';
import { L10n } from '../../../ts/L10n/L10n';
import { BackgroundJobDefinition } from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { GetBackgroundJobStatisticsRequest, IGetBackgroundJobStatisticsResult } from '../../../ts/ServerApi/requests/GetBackgroundJobStatisticsRequest';
import { IBackgroundJobStatistics } from '../../../ts/ServerApi/types/IBackgroundJobStatistics';
import { IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { ErrorNotifyingServer } from '../../../ts/services/ErrorNotifyingServer';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { SpaceContentsRequestAction } from '../../actions/spaces/RequestSpaceContentsActions';
import { ServiceLocator } from '../../ServiceLocator';
import { ExplorerEmptyStateProps } from '../Components/ExplorerEmpyState';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { ExplorerTestIds } from '../Explorer/Explorer';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BackgroundJobStyles } from './BackgroundJobStyles';


export interface BackgroundJobsSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly statusFilter?: IStringValuedFilterClause;
}

export interface BackgroundJobsSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => SpaceContentsRequestAction;
}

export interface BackgroundJobsSpaceOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly telemetry: Telemetry;
}

export type BackgroundJobsSpaceProps = BackgroundJobsSpaceStateProps & BackgroundJobsSpaceDispatchProps & BackgroundJobsSpaceOwnProps;

interface BackgroundJobsState {
  statistics?: IBackgroundJobStatistics;
}

export class BackgroundJobsSpace extends React.Component<BackgroundJobsSpaceProps, BackgroundJobsState> {
  constructor(props: BackgroundJobsSpaceProps) {
    super(props);
    this.state = {};
  }

  public render() {
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={BackgroundJobStyles.MastheadArea}>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.jobsBrowserTitle()}</div>
          </div>
          <div>
            <div className={BackgroundJobStyles.SummaryInfoButtonGroup}>
              {this.state.statistics && <>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsFailed({ COUNT_LOC: L10n.localizedCount(this.state.statistics.failedBackgroundJobs) })}
                </span>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsCompleted({ COUNT_LOC: L10n.localizedCount(this.state.statistics.succeededBackgroundJobs) })}
                </span>
                <span style={BackgroundJobStyles.SummaryInfoItem}>
                  {Messages.backgroundJobStatisticsCancelled({ COUNT_LOC: L10n.localizedCount(this.state.statistics.cancelledBackgroundJobs) })}
                </span>
              </>
              }
            </div>
          </div>
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            angularContext={this.props.angularContext}
            supportedDisplayModes={BackgroundJobDefinition.supportedDisplayModes}
            telemetry={this.props.telemetry}
            emptyStateProps={this.emptyStateProps()}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              hideSelection: true,
            }}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const server = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const siteId = BackgroundJobDefinition.defaultFilters('').siteId;
    const filter = isNullOrUndefined(siteId) ? undefined : Filter.and([siteId]);
    server.sendRequest(new GetBackgroundJobStatisticsRequest({filter}))
      .then((result: IGetBackgroundJobStatisticsResult) => this.setState({
        statistics: result
      }));
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: BackgroundJobsSpaceProps) {
    if(!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      dataTestId: ExplorerTestIds.ExplorerNoContent,
      iconUrl: '',
      primaryMessage: Messages.contentEmptyMessage(),
      secondaryMessage: '',
    };
  }

  private onNoContentAction = (): void => {
    // Currently there are no actions that can be performed on background jobs
    // See TFS 872202 for the first expected action to be added
  }
}
